import React, { useEffect, useMemo, useState } from "react";
import {
	listPrograms,
	saveProgram,
	deleteProgram,
	assignProgram,
	getCoachTemplates,
	getBestSeriesForExercises,
	getTemplateFolders,
} from "../../services/authService";
import ProgramCellProgressionPanel from "./ProgramCellProgressionPanel";
import { arrondiMax, rpeLisible } from "../../utils/progressionSerie";
import { formatWeight } from "../../utils/weight";
import TemplatePickerModal from "./TemplatePickerModal";
import useAlerteDepart from "../../utils/useAlerteDepart";

const DAY_NAMES = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];


// ─── Modale éditeur de programme ───
function ProgramEditorModal({ program, coachId, coachTemplates, coachFolders, coachAssoc, onClose, onSaved, onNotif, onCreateNewTemplate }) {
	const [nom, setNom] = useState(program?.nom || "");
	const [description, setDescription] = useState(program?.description || "");
	const [dureeSemaines, setDureeSemaines] = useState(program?.dureeSemaines || 4);
	// entries : Map "semaine-jour" -> idTemplate (une entrée par cellule pour le MVP)
	const [entries, setEntries] = useState(() => {
		const m = new Map();
		(program?.entries || []).forEach((e) => {
			m.set(`${e.semaine}-${e.jour}`, e.idTemplate);
		});
		return m;
	});
	// Paramétrage de progression : array plat de tous les paramètres, tel que consommé
	// par l'API. Indexé par cellule via un dérivé mémoïsé pour le rendu.
	const [serieParams, setSerieParams] = useState(() => program?.serieParams || []);
	// Cellule en cours de paramétrage (ouvre ProgramCellProgressionPanel).
	// { semaine, jour, idTemplate, templateName } | null
	const [progCell, setProgCell] = useState(null);
	// Cellule en cours de choix de template (ouvre TemplatePickerModal).
	// { semaine, jour } | null
	const [pickerCell, setPickerCell] = useState(null);
	const [saving, setSaving] = useState(false);
	// Une modification pas encore enregistrée : fermer la modale (croix,
	// Annuler, clic à côté) demande confirmation, quitter la page aussi.
	const [modifie, setModifie] = useState(false);
	useAlerteDepart(modifie);

	const demanderFermeture = () => {
		if (modifie && !window.confirm("Les modifications du programme ne sont pas enregistrées. Fermer quand même ?")) return;
		onClose?.();
	};

	const templatesById = useMemo(() => {
		const m = new Map();
		(coachTemplates || []).forEach((t) => m.set(t.Id ?? t.id, t.Nom ?? t.nom ?? "Sans nom"));
		return m;
	}, [coachTemplates]);

	// Index rapide "cellule → true" si au moins un paramètre existe pour elle.
	// Sert à afficher la pastille "programmé" vs "à définir" dans la grille.
	const cellHasParams = useMemo(() => {
		const s = new Set();
		serieParams.forEach((p) => s.add(`${p.semaine}-${p.jour}`));
		return s;
	}, [serieParams]);

	const setCell = (semaine, jour, tplId) => {
		const key = `${semaine}-${jour}`;
		setModifie(true);
		setEntries((prev) => {
			const next = new Map(prev);
			if (tplId == null || tplId === "") next.delete(key);
			else next.set(key, Number(tplId));
			return next;
		});
		// Si on vide/change le template d'une cellule, on drop ses params (ils
		// pointaient sur le contenu de l'ancien template).
		if (tplId == null || tplId === "") {
			setSerieParams((prev) => prev.filter((p) => !(p.semaine === semaine && p.jour === jour)));
		}
	};

	const openProgPanel = (semaine, jour, tplId) => {
		setProgCell({
			semaine, jour,
			idTemplate: tplId,
			templateName: templatesById.get(tplId) || "Séance",
		});
	};

	// Cases qui portent le même modèle que la cellule paramétrée : son réglage
	// peut y être copié (mêmes exercices, mêmes séries).
	const casesMemeModele = useMemo(() => {
		if (!progCell) return [];
		const cases = [];
		for (const [key, tplId] of entries.entries()) {
			const [semaine, jour] = key.split("-").map(Number);
			if (Number(tplId) !== Number(progCell.idTemplate)) continue;
			if (semaine === progCell.semaine && jour === progCell.jour) continue;
			cases.push({ semaine, jour, reglee: cellHasParams.has(key) });
		}
		return cases.sort((a, b) => a.semaine - b.semaine || a.jour - b.jour);
	}, [progCell, entries, cellHasParams]);

	const handleProgSaved = (paramsForCell, cibles = []) => {
		setModifie(true);
		// Remplace intégralement les params de la cellule (source de vérité = ce
		// que le panneau vient de renvoyer), et ceux des cases où le coach a
		// choisi de copier le réglage.
		const cases = [{ semaine: progCell.semaine, jour: progCell.jour }, ...cibles];
		const estCible = (p) => cases.some((c) => c.semaine === p.semaine && c.jour === p.jour);
		setSerieParams((prev) => [
			...prev.filter((p) => !estCible(p)),
			...cases.flatMap((c) => paramsForCell.map((p) => ({ ...p, semaine: c.semaine, jour: c.jour }))),
		]);
	};

	const handleSave = async () => {
		if (!nom.trim()) {
			onNotif?.("Nom requis", "error");
			return;
		}
		setSaving(true);
		const entriesArr = [];
		let ordre = 0;
		for (const [key, tplId] of entries.entries()) {
			const [s, j] = key.split("-").map(Number);
			entriesArr.push({ idTemplate: tplId, semaine: s, jour: j, ordre: ordre++ });
		}
		// Nettoie les params orphelins (cellules qui n'existent plus).
		const activeCells = new Set([...entries.keys()]);
		const cleanParams = serieParams.filter((p) => activeCells.has(`${p.semaine}-${p.jour}`));

		const res = await saveProgram({
			id: program?.id || null,
			nom: nom.trim(),
			description,
			dureeSemaines,
			entries: entriesArr,
			serieParams: cleanParams,
		});
		setSaving(false);
		if (res.success) {
			onNotif?.(program?.id ? "Programme mis à jour" : "Programme créé");
			onSaved?.();
			onClose?.();
		} else {
			onNotif?.(res.error || "Erreur", "error");
		}
	};

	return (
		// Seul un clic sur le fond lui-même ferme : un clic à côté d'une fenêtre
		// ouverte par-dessus (choix du modèle, progression) remonte jusqu'ici.
		<div className="mens-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !saving) demanderFermeture(); }}>
			<div className="mens-modal prg-editor-modal" onClick={(e) => e.stopPropagation()}>
				<div className="mens-modal-header prg-editor-modal-header">
					<h3>{program?.id ? "Modifier le programme" : "Nouveau programme"}</h3>
					<button className="mens-modal-close" onClick={demanderFermeture} disabled={saving}>×</button>
				</div>

				{/* Zone haute figée : métadonnées du programme. Ne scrolle pas pour
				    rester toujours accessible, même quand la grille est longue. */}
				<div className="prg-editor-modal-top">
					<div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 12 }}>
						<div>
							<label className="mens-modal-label">Nom</label>
							<input type="text" value={nom} onChange={(e) => { setNom(e.target.value); setModifie(true); }} className="mens-modal-input" placeholder="Ex : Force 4 semaines" autoFocus />
						</div>
						<div>
							<label className="mens-modal-label">Semaines</label>
							<input
								type="number"
								min={1}
								max={52}
								value={dureeSemaines}
								onChange={(e) => { setDureeSemaines(Math.max(1, Math.min(52, parseInt(e.target.value) || 1))); setModifie(true); }}
								className="mens-modal-input"
							/>
						</div>
					</div>
					<div style={{ marginTop: 10 }}>
						<label className="mens-modal-label">Description (optionnelle)</label>
						<textarea
							value={description}
							onChange={(e) => { setDescription(e.target.value); setModifie(true); }}
							className="mens-modal-input"
							rows={2}
							placeholder="Notes internes sur ce cycle"
						/>
					</div>
					<div className="bulk-assign-step" style={{ marginTop: 12 }}>
						<span className="bulk-assign-step-num">1</span>
						<span className="bulk-assign-step-label">Grille : sélectionne un modèle par jour</span>
					</div>
				</div>

				{/* Zone scrollable au centre : la grille grandit avec le nombre de
				    semaines mais scrolle en interne — la modale garde sa hauteur. */}
				<div className="prg-editor-modal-scroll">
					<div style={{ overflowX: "auto", border: "1px solid var(--line-soft)", borderRadius: 8, padding: 8 }}>
						<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
							<thead>
								<tr>
									<th style={{ padding: 6, textAlign: "left", color: "var(--ink-faint)", fontWeight: 600, width: 70 }}>Semaine</th>
									{DAY_NAMES.map((d, i) => (
										<th key={i} style={{ padding: 6, textAlign: "center", color: "var(--ink-soft)", fontWeight: 600 }}>{d}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{Array.from({ length: dureeSemaines }, (_, wi) => wi + 1).map((week) => (
									<tr key={week}>
										<td style={{ padding: 6, fontWeight: 700, color: "var(--ink)", background: "rgba(0,0,0,0.02)" }}>S{week}</td>
										{DAY_NAMES.map((_, di) => {
											const jour = di + 1;
											const key = `${week}-${jour}`;
											const tplId = entries.get(key) || "";
											const hasParams = tplId && cellHasParams.has(key);
											const tplName = tplId ? (templatesById.get(Number(tplId)) || "Modèle") : "";
											return (
												<td key={di} style={{ padding: 3 }}>
													<div className={`prg-cell ${tplId ? "filled" : ""} ${hasParams ? "programmed" : ""}`}>
														{!tplId ? (
															<button
																type="button"
																className="prg-cell-add"
																title="Choisir un modèle"
																onClick={() => setPickerCell({ semaine: week, jour })}
															>
																<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
																	<line x1="12" y1="5" x2="12" y2="19" />
																	<line x1="5" y1="12" x2="19" y2="12" />
																</svg>
															</button>
														) : (
															<>
																{/* Nom cliquable = rouvre le picker pour changer.
																    Le picker garde le tpl actuel matérialisé. */}
																<button
																	type="button"
																	className="prg-cell-name"
																	title={`${tplName} — cliquer pour changer`}
																	onClick={() => setPickerCell({ semaine: week, jour })}
																>
																	{tplName}
																</button>
																<div className="prg-cell-foot">
																	<span className={`prg-cell-status ${hasParams ? "on" : "off"}`}>
																		{hasParams ? "● programmé" : "à définir"}
																	</span>
																	<div className="prg-cell-actions">
																		<button
																			type="button"
																			className="prg-cell-cog"
																			title="Paramétrer la progression"
																			onClick={(e) => { e.stopPropagation(); openProgPanel(week, jour, Number(tplId)); }}
																		>
																			<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
																				<circle cx="12" cy="12" r="3"/>
																				<path d="M19.4 15a1.6 1.6 0 0 0 .3 1.7l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.6 1.6 0 0 0-1.7-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.09A1.6 1.6 0 0 0 9 19.4a1.6 1.6 0 0 0-1.7.3l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.6 1.6 0 0 0 4.6 15a1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.09A1.6 1.6 0 0 0 4.6 9a1.6 1.6 0 0 0-.3-1.7l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.6 1.6 0 0 0 9 4.6a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.09a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.7-.3l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.6 1.6 0 0 0-.3 1.7 1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.09a1.6 1.6 0 0 0-1.5 1z"/>
																			</svg>
																		</button>
																		<button
																			type="button"
																			className="prg-cell-clear"
																			title="Retirer le modèle de cette cellule"
																			onClick={(e) => { e.stopPropagation(); setCell(week, jour, ""); }}
																		>
																			×
																		</button>
																	</div>
																</div>
															</>
														)}
													</div>
												</td>
											);
										})}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>

				{/* Compteur figé sous la zone scroll : le coach voit toujours combien
				    de séances il a placées, même sur un programme de 12+ semaines. */}
				<div className="prg-editor-modal-count">
					{entries.size} séance{entries.size > 1 ? "s" : ""} programmée{entries.size > 1 ? "s" : ""} · {templatesById.size} modèles dispo
				</div>

				<div className="mens-modal-actions prg-editor-modal-actions">
					<button className="mens-btn ghost" onClick={demanderFermeture} disabled={saving}>Annuler</button>
					<button className="mens-btn primary" onClick={handleSave} disabled={saving || !nom.trim()}>
						{saving ? "Enregistrement…" : "Enregistrer"}
					</button>
				</div>
			</div>

			{progCell && (
				<ProgramCellProgressionPanel
					cell={progCell}
					coachId={coachId}
					existingParams={serieParams.filter((p) => p.semaine === progCell.semaine && p.jour === progCell.jour)}
					casesMemeModele={casesMemeModele}
					onSave={handleProgSaved}
					onClose={() => setProgCell(null)}
					onNotif={onNotif}
				/>
			)}

			{pickerCell && (
				<TemplatePickerModal
					coachTemplates={coachTemplates}
					coachFolders={coachFolders}
					coachAssoc={coachAssoc}
					currentTemplateId={Number(entries.get(`${pickerCell.semaine}-${pickerCell.jour}`)) || null}
					onPick={(tpl) => {
						const id = tpl.Id ?? tpl.id;
						setCell(pickerCell.semaine, pickerCell.jour, id);
						setPickerCell(null);
					}}
					onCreateNew={onCreateNewTemplate ? () => {
						// Confirm AVANT toute fermeture pour que l'annulation laisse le
						// picker ouvert (sinon le coach perd son contexte et doit recliquer
						// la cellule). Si accepté : fermeture picker + modale programme,
						// puis remonte au parent qui fait la nav.
						const confirmed = window.confirm(
							"Créer un nouveau modèle va fermer l'édition du programme en cours.\n\nLes modifications non enregistrées seront perdues. Continuer ?"
						);
						if (!confirmed) return;
						setPickerCell(null);
						onClose();
						onCreateNewTemplate();
					} : null}
					onClose={() => setPickerCell(null)}
				/>
			)}
		</div>
	);
}

// ─── Modale d'affectation programme → élèves ───
// Multi-étapes :
//   1. Élèves + date de démarrage
//   2. Renseigner les maxes des élèves (skippée si aucun exo n'est en mode "pct")
//   3. Confirmation + création
function ProgramAssignModal({ program, students, onClose, onNotif }) {
	const [step, setStep] = useState(1);
	const [studentIds, setStudentIds] = useState([]);
	const [startDate, setStartDate] = useState(() => {
		const d = new Date();
		const day = d.getDay();
		const diff = day === 0 ? -6 : 1 - day;
		d.setDate(d.getDate() + diff);
		return d.toISOString().split("T")[0];
	});
	// Maxes : Map<`${studentId}-${exoId}`, { value: string, source: "estimated"|"manual"|"none" }>
	// On stocke la valeur en string pour rester compatible avec un input contrôlé.
	const [maxes, setMaxes] = useState(new Map());
	// Métadonnées récupérées via getBestSeriesForExercises pour l'affichage sous-titre.
	// Map<`${studentId}-${exoId}`, { reps, poids, date, epley }>
	const [bestMeta, setBestMeta] = useState(new Map());
	// Nom d'un exo (côté catalogue coach), remonté par getBestSeriesForExercises.
	// Map<exoId, string>. Sert à afficher un nom lisible à l'étape 2.
	const [exoNames, setExoNames] = useState(new Map());
	const [loadingMaxes, setLoadingMaxes] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [result, setResult] = useState(null);

	const toggle = (id) => setStudentIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
	const perStudent = program?.entries?.length || 0;
	const total = studentIds.length * perStudent;

	// Exos du programme qui sont configurés en mode "pct" (au moins une série).
	// Dédupliqués : le max est saisi une fois par exo, pas par série.
	const pctExoIds = useMemo(() => {
		const s = new Set();
		(program?.serieParams || []).forEach((p) => {
			if (p.modeCharge === "pct") s.add(p.idReferentielExercice);
		});
		return Array.from(s);
	}, [program]);

	const needsMaxStep = pctExoIds.length > 0;

	// Étape 2 : au montage, fetch les meilleures séries pour tous les élèves × exos
	// concernés, pré-remplit les maxes avec le 1RM estimé renvoyé par l'API
	// (Epley, reps en réserve comprises quand le RPE est saisi), arrondi à 2,5 kg.
	useEffect(() => {
		if (step !== 2) return;
		if (!needsMaxStep || studentIds.length === 0) return;
		let cancelled = false;
		setLoadingMaxes(true);
		Promise.all(studentIds.map((sid) =>
			getBestSeriesForExercises(sid, pctExoIds).then((res) => ({ sid, data: res.data || [] }))
		)).then((results) => {
			if (cancelled) return;
			const newMaxes = new Map(maxes);
			const newMeta = new Map();
			const newNames = new Map(exoNames);
			results.forEach(({ sid, data }) => {
				data.forEach((entry) => {
					const key = `${sid}-${entry.exerciseId}`;
					// Ne pas écraser une valeur déjà saisie manuellement (retour arrière possible).
					if (!newMaxes.has(key)) {
						const est = arrondiMax(entry.series?.epley);
						if (est > 0) {
							newMaxes.set(key, { value: String(est), source: "estimated" });
						} else {
							newMaxes.set(key, { value: "", source: "none" });
						}
					}
					if (entry.series) newMeta.set(key, entry.series);
					if (entry.name) newNames.set(entry.exerciseId, entry.name);
				});
			});
			setMaxes(newMaxes);
			setBestMeta(newMeta);
			setExoNames(newNames);
			setLoadingMaxes(false);
		}).catch(() => {
			if (cancelled) return;
			setLoadingMaxes(false);
		});
		return () => { cancelled = true; };
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [step, needsMaxStep, studentIds, pctExoIds]);

	const editMax = (studentId, exoId, value) => {
		setMaxes((prev) => {
			const next = new Map(prev);
			next.set(`${studentId}-${exoId}`, { value, source: "manual" });
			return next;
		});
	};

	const goNext = () => {
		if (step === 1) {
			if (studentIds.length === 0) return;
			setStep(needsMaxStep ? 2 : 3);
		} else if (step === 2) {
			setStep(3);
		}
	};
	const goPrev = () => {
		if (step === 3) setStep(needsMaxStep ? 2 : 1);
		else if (step === 2) setStep(1);
	};

	const handleConfirm = async () => {
		if (studentIds.length === 0) return;
		setSubmitting(true);
		const startTs = new Date(startDate + "T12:00:00").getTime();
		// Sérialise les maxes valides (>0). Un max vide/0 = fallback dernière charge élève sinon 0.
		const maxesArr = [];
		maxes.forEach(({ value }, key) => {
			const v = parseFloat(value);
			if (isNaN(v) || v <= 0) return;
			const [sid, exoId] = key.split("-").map(Number);
			if (studentIds.includes(sid)) {
				maxesArr.push({ studentId: sid, idReferentielExercice: exoId, max: v });
			}
		});
		const res = await assignProgram({ idProgramme: program.id, studentIds, startDate: startTs, maxes: maxesArr });
		setSubmitting(false);
		if (res.success) {
			setResult({ ok: true, created: res.created });
			onNotif?.(`${res.created} séance${res.created > 1 ? "s" : ""} créée${res.created > 1 ? "s" : ""}`);
		} else {
			setResult({ ok: false, error: res.error });
			onNotif?.(res.error || "Erreur", "error");
		}
	};

	const stepCount = needsMaxStep ? 3 : 2;
	const stepIdxShown = step === 3 && !needsMaxStep ? 2 : step;

	return (
		<div className="mens-modal-overlay" onClick={submitting ? undefined : onClose}>
			<div className="mens-modal assign-modal" onClick={(e) => e.stopPropagation()}>
				<div className="mens-modal-header assign-modal-header">
					<h3>Affecter « {program?.nom} »</h3>
					<div className="assign-stepper">
						{Array.from({ length: stepCount }, (_, i) => i + 1).map((n) => (
							<React.Fragment key={n}>
								<span className={`assign-step ${n === stepIdxShown ? "active" : n < stepIdxShown ? "done" : ""}`}>
									{n}
								</span>
								{n < stepCount && <span className="assign-stepper-line" />}
							</React.Fragment>
						))}
					</div>
					<button className="mens-modal-close" onClick={onClose} disabled={submitting}>×</button>
				</div>

				{/* ─── Étape 1 : élèves + date ─── */}
				{step === 1 && (
					<div className="assign-modal-body">
						<div>
							<label className="mens-modal-label">Date de démarrage (lundi de la semaine 1)</label>
							<input
								type="date"
								value={startDate}
								onChange={(e) => setStartDate(e.target.value)}
								className="mens-modal-input"
							/>
						</div>
						<div style={{ marginTop: 12 }}>
							<div className="bulk-assign-step">
								<span className="bulk-assign-step-num">1</span>
								<span className="bulk-assign-step-label">Élèves ({studentIds.length})</span>
								<div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
									<button type="button" className="bulk-assign-mini-btn" onClick={() => setStudentIds((students || []).map((s) => s.id))}>Tous</button>
									<button type="button" className="bulk-assign-mini-btn" onClick={() => setStudentIds([])}>Aucun</button>
								</div>
							</div>
							<div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid var(--line-soft)", borderRadius: 8, padding: 4 }}>
								{(students || []).map((s) => {
									const active = studentIds.includes(s.id);
									return (
										<button key={s.id} type="button" onClick={() => toggle(s.id)} className={`bulk-assign-student-item ${active ? "active" : ""}`}>
											<span className="cbox">{active ? "☑" : "☐"}</span>
											<span>{s.prenom} {s.nom}</span>
										</button>
									);
								})}
							</div>
						</div>
					</div>
				)}

				{/* ─── Étape 2 : renseigner les maxes ─── */}
				{step === 2 && (
					<div className="assign-modal-body">
						<div className="assign-help">
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
							</svg>
							<span>
								Ce programme utilise des charges <b>en % du 1RM</b>. Renseigne le max de chaque élève sur ces exos — les charges seront calculées et arrondies au 2,5 kg.
								Un champ laissé vide bascule sur la dernière charge de l'élève, sinon 0.
							</span>
						</div>

						{loadingMaxes && <div style={{ padding: 20, textAlign: "center", color: "var(--ink-faint)" }}>Chargement des historiques…</div>}

						{!loadingMaxes && pctExoIds.map((exoId) => {
							const exoName = exoNames.get(exoId) || `Exercice #${exoId}`;
							return (
								<div key={exoId} className="assign-exo-group">
									<div className="assign-exo-group-head">
										<div className="assign-exo-group-name">
											{exoName}
											<span className="assign-exo-group-tag">% du max</span>
										</div>
										<span className="assign-exo-group-count">{studentIds.length} élève{studentIds.length > 1 ? "s" : ""}</span>
									</div>
									{studentIds.map((sid) => {
										const student = (students || []).find((s) => s.id === sid);
										const key = `${sid}-${exoId}`;
										const entry = maxes.get(key) || { value: "", source: "none" };
										const meta = bestMeta.get(key);
										return (
											<div key={sid} className="assign-student-row">
												<div className="assign-student-ident">
													<span className="assign-student-avatar">{(student?.prenom?.[0] || "?") + (student?.nom?.[0] || "")}</span>
													<div>
														<span className="assign-student-name">{student?.prenom} {student?.nom}</span>
														<span className="assign-student-sub">
															{meta
																? `meilleure série : ${meta.reps} × ${formatWeight(meta.poids, 0)} kg${rpeLisible(meta.rpe) ? ` · RPE ${rpeLisible(meta.rpe)}` : ""}`
																: "pas d'historique sur cet exo"}
														</span>
													</div>
												</div>
												<div className="assign-max-field">
													{entry.source === "estimated" && <span className="assign-max-badge estimated">≈ estimé</span>}
													{entry.source === "none" && <span className="assign-max-badge fallback">fallback 0 kg</span>}
													<span className={`assign-max-input ${entry.source === "estimated" ? "estimated" : ""}`}>
														<input
															type="number"
															min={0}
															step={2.5}
															value={entry.value}
															placeholder="—"
															onChange={(e) => editMax(sid, exoId, e.target.value)}
														/>
														<span className="unit">kg</span>
													</span>
												</div>
											</div>
										);
									})}
								</div>
							);
						})}
					</div>
				)}

				{/* ─── Étape 3 : confirmation ─── */}
				{step === 3 && (
					<div className="assign-modal-body">
						<div style={{ padding: 14, borderRadius: 8, background: "rgba(48,164,154,0.06)", border: "1px solid rgba(48,164,154,0.2)", fontSize: 14 }}>
							Prêt à créer <strong>{total}</strong> séance{total > 1 ? "s" : ""} au total
							({perStudent} par élève × {studentIds.length} élève{studentIds.length > 1 ? "s" : ""}).
							{needsMaxStep && (
								<div style={{ marginTop: 8, fontSize: 12, color: "var(--ink-soft)" }}>
									{Array.from(maxes.values()).filter((m) => parseFloat(m.value) > 0).length} max renseigné{Array.from(maxes.values()).filter((m) => parseFloat(m.value) > 0).length > 1 ? "s" : ""} ·
									{" "}les charges des séries en % seront calculées à partir de ces valeurs, arrondies au 2,5 kg.
								</div>
							)}
						</div>

						{result && (
							<div className={result.ok ? "mens-modal-success" : "mens-modal-error"} style={{ marginTop: 12 }}>
								{result.ok
									? `${result.created} séance${result.created > 1 ? "s" : ""} créée${result.created > 1 ? "s" : ""}`
									: (result.error || "Erreur")}
							</div>
						)}
					</div>
				)}

				<div className="mens-modal-actions">
					{step > 1 && !result?.ok && (
						<button className="mens-btn ghost" onClick={goPrev} disabled={submitting}>← Retour</button>
					)}
					<button className="mens-btn ghost" onClick={onClose} disabled={submitting}>{result?.ok ? "Fermer" : "Annuler"}</button>
					{step < 3 && !result?.ok && (
						<button
							className="mens-btn primary"
							onClick={goNext}
							disabled={(step === 1 && studentIds.length === 0) || loadingMaxes}
						>
							Étape suivante →
						</button>
					)}
					{step === 3 && !result?.ok && (
						<button className="mens-btn primary" onClick={handleConfirm} disabled={submitting}>
							{submitting ? "Création…" : `Affecter (${total})`}
						</button>
					)}
				</div>
			</div>
		</div>
	);
}

// ─── Page principale ───
// onCreateNewTemplate : callback optionnel de CoachMode pour rediriger vers
// l'éditeur de nouveau template quand le coach l'appelle depuis le picker.
export default function CoachPrograms({ user, students, onNotif, onCreateNewTemplate }) {
	const [programs, setPrograms] = useState([]);
	const [coachTemplates, setCoachTemplates] = useState([]);
	// Dossiers coach + associations : nécessaires au picker de templates pour
	// filtrer par dossier (cohérent avec la modale d'import existante).
	const [coachFolders, setCoachFolders] = useState([]);
	const [coachAssoc, setCoachAssoc] = useState([]);
	const [loading, setLoading] = useState(true);
	const [editing, setEditing] = useState(null);       // program | {} pour nouveau
	const [assigning, setAssigning] = useState(null);   // program

	const refresh = async () => {
		setLoading(true);
		const [pRes, tRes, fRes] = await Promise.all([
			listPrograms(),
			getCoachTemplates(),
			getTemplateFolders(),
		]);
		setPrograms(pRes.success ? pRes.programs : []);
		setCoachTemplates(tRes.success ? (tRes.data?.templates || []) : []);
		if (fRes.success) {
			setCoachFolders(fRes.folders || []);
			setCoachAssoc(fRes.associations || []);
		}
		setLoading(false);
	};

	useEffect(() => { refresh(); }, []);

	const handleDelete = async (p) => {
		if (!window.confirm(`Supprimer le programme « ${p.nom} » ?`)) return;
		const res = await deleteProgram(p.id);
		if (res.success) {
			onNotif?.("Programme supprimé");
			refresh();
		} else {
			onNotif?.("Erreur lors de la suppression", "error");
		}
	};

	return (
		<div className="coach-programs-page">
			<div className="coach-programs-head">
				<div>
					<h2 style={{ margin: 0, fontSize: 22 }}>Programmes</h2>
					<div style={{ color: "var(--ink-soft)", fontSize: 13, marginTop: 4 }}>
						Cycles de plusieurs semaines à affecter en un clic à tes élèves
					</div>
				</div>
				<button className="mens-btn primary" onClick={() => setEditing({})}>+ Nouveau programme</button>
			</div>

			{loading ? (
				<div className="coach-exercises-library-empty">Chargement…</div>
			) : programs.length === 0 ? (
				<div className="coach-exercises-library-empty">
					Aucun programme pour l'instant. Crée ton premier cycle.
				</div>
			) : (
				<div className="coach-programs-grid">
					{programs.map((p) => (
						<div key={p.id} className="coach-programs-card">
							<div className="coach-programs-card-head">
								<div>
									<div className="coach-programs-card-name">{p.nom}</div>
									<div className="coach-programs-card-meta">
										{p.dureeSemaines} semaine{p.dureeSemaines > 1 ? "s" : ""} · {p.entries.length} séance{p.entries.length > 1 ? "s" : ""}
									</div>
								</div>
								<div style={{ display: "flex", gap: 6 }}>
									<button className="mens-btn ghost" onClick={() => setEditing(p)} style={{ fontSize: 12 }}>Modifier</button>
									<button className="mens-btn primary" onClick={() => setAssigning(p)} style={{ fontSize: 12 }} disabled={p.entries.length === 0}>Affecter</button>
									<button
										type="button"
										onClick={() => handleDelete(p)}
										title="Supprimer"
										style={{ width: 30, height: 30, borderRadius: 7, background: "transparent", border: "1px solid rgba(244,67,54,0.30)", color: "rgba(244,67,54,0.80)", cursor: "pointer" }}
									>×</button>
								</div>
							</div>
							{p.description && <div className="coach-programs-card-desc">{p.description}</div>}
							<div className="coach-programs-card-grid">
								{Array.from({ length: p.dureeSemaines }, (_, w) => (
									<div key={w} className="coach-programs-card-week">
										<div className="coach-programs-card-weeknum">S{w + 1}</div>
										<div className="coach-programs-card-days">
											{DAY_NAMES.map((d, di) => {
												const entry = p.entries.find((e) => e.semaine === w + 1 && e.jour === di + 1);
												return (
													<div key={di} className={`coach-programs-card-day ${entry ? "filled" : ""}`} title={entry ? "Séance programmée" : ""}>
														{d.charAt(0)}
													</div>
												);
											})}
										</div>
									</div>
								))}
							</div>
						</div>
					))}
				</div>
			)}

			{editing != null && (
				<ProgramEditorModal
					program={editing.id ? editing : null}
					coachId={user?.id}
					coachTemplates={coachTemplates}
					coachFolders={coachFolders}
					coachAssoc={coachAssoc}
					onClose={() => setEditing(null)}
					onSaved={refresh}
					onNotif={onNotif}
					onCreateNewTemplate={onCreateNewTemplate ? () => {
						// Le confirm est géré au plus près de l'action (dans le picker)
						// pour que l'annulation garde l'état intact. Ici on ne fait que
						// dépiler : la modale est déjà fermée via onClose.
						setEditing(null);
						onCreateNewTemplate();
					} : null}
				/>
			)}

			{assigning && (
				<ProgramAssignModal
					program={assigning}
					students={students}
					onClose={() => setAssigning(null)}
					onNotif={onNotif}
				/>
			)}
		</div>
	);
}
